import { FormControl, FormGroup } from "@angular/forms";
import { ApiError, ApiResponse, HttpStatusCodesEnum, PrizeGroupCreateRequestModel, PrizeGroupResponseInterface, PrizeGroupResponseModel, PrizeGroupUpdateRequestModel, ServerResponse, TradingPlanResponseModel, TradingPlanService } from "@spa/api";
import { AmountMaxValidatorKey, AmountMinValidatorKey } from "@spa/validators";
import { PrizeGroupControls, PrizeGroupGroupPercentageSelector } from "appAssets/descriptors";
import { PanelPlansEditMessages } from "appAssets/messages";
import { TradingPlanFormErrorStatesEnum } from "appEntries/panel/enums";
import { BaseControl, Helper } from "appShared/classes";
import { switchMap } from "rxjs/operators";

export class PrizeGroupForm {
    private tradingPlanService: TradingPlanService;
    private plan: TradingPlanResponseModel;
    private model: PrizeGroupResponseModel;

    public formGroup: FormGroup;
    public controls: BaseControl[];
    FORM_ELEMENT_CCOUNT: number;

    constructor(tradingPlanService: TradingPlanService, plan: TradingPlanResponseModel, model: PrizeGroupResponseModel) {
        this.tradingPlanService = tradingPlanService;
        this.plan = plan;
        this.model = model;
    }

    initForm(): void {

        this.formGroup = new FormGroup({});

        this.controls = PrizeGroupControls(this.model)
            .map((control: BaseControl<string | number>) => {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
                return control;
            });
        this.formGroup.get(PrizeGroupResponseModel.getPerPositionSelector()).valueChanges.subscribe((e) => {
            this.onPerPositionChanged(e);
        });
        this.formGroup.get(PrizeGroupGroupPercentageSelector).valueChanges.subscribe((e) => {
            this.onForGroupChanged(e);
        });
    }

    onPerPositionChanged(e:number){
        const currentData = this.createTempModel();
        const forGroupControl = this.formGroup.get(PrizeGroupGroupPercentageSelector);
        let val = 0
        if (!e) {
            val = 0;
        } else {
            val = currentData.getPositionsCount() * e;
        }
        forGroupControl.patchValue(val, { emitEvent: false });
    }

    createTempModel(): PrizeGroupResponseModel {
       return new PrizeGroupResponseModel(
            {
                start_position: Number(this.formGroup.value[PrizeGroupResponseModel.getStartPositionSelector()]),
                id: this.model?.id,
                per_position: Number(this.formGroup.value[PrizeGroupResponseModel.getPerPositionSelector()]),
                end_position: Number(this.formGroup.value[PrizeGroupResponseModel.getEndPositionSelector()]),
                trading_plan_id: this.plan.id,
                trace_identifier: this.model?.trace_identifier
            }
        );
    }

    onForGroupChanged(e:number){
        const currentData = this.createTempModel();
        const positionControl = this.formGroup.get(PrizeGroupResponseModel.getPerPositionSelector());
        let val = 0
        if (!e || currentData.getPositionsCount() == 0){
            val = 0;
        }else{
            val = Math.floor((e / currentData.getPositionsCount()) * 100) / 100;
        }
        positionControl.patchValue(val, { emitEvent: false });
    }

    createRequest(): ServerResponse<PrizeGroupResponseModel> {
        if (!this.formGroup.dirty) {
            return null;
        }
        let request = null;
        if (this.model == null) {
            const requestmodel = new PrizeGroupCreateRequestModel(
                this.formGroup.value[PrizeGroupCreateRequestModel.getStartPositionSelector()] as number,
                this.formGroup.value[PrizeGroupCreateRequestModel.getEndPositionSelector()] as number,
                this.formGroup.value[PrizeGroupCreateRequestModel.getPerPositionSelector()] as number,
                this.plan.id,
                null
            );
            request = this.tradingPlanService.createPrizeGroup(requestmodel.serialize());
        } else {
            const requestmodel = new PrizeGroupUpdateRequestModel(
                this.formGroup.value[PrizeGroupUpdateRequestModel.getStartPositionSelector()] as number,
                this.formGroup.value[PrizeGroupUpdateRequestModel.getEndPositionSelector()] as number,
                this.formGroup.value[PrizeGroupUpdateRequestModel.getPerPositionSelector()] as number,
                this.model.id,
                null
            );
            request = this.tradingPlanService.updatePrizeGroup(requestmodel.serialize());
        }
        return request.pipe(switchMap((registerResponse: ApiResponse<ApiError[] | PrizeGroupResponseInterface>) => {
            if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<PrizeGroupResponseInterface>(
                    registerResponse,
                    TradingPlanFormErrorStatesEnum.CreatePlanFailed
                );
            }

            return Helper.createSuccessResponseObservable<PrizeGroupResponseInterface>(registerResponse.data as PrizeGroupResponseInterface);

        }));
    }

    validateForm(messagesFunction: (controlsLabel?: string, controlLength?: number) => { [key: string]: string }): boolean {
        const statusesCheck = this.controls.map((control: BaseControl) => {
            const formControl = this.formGroup.controls[control.name];
            if (formControl && formControl.invalid) {
                control.errors = Object.keys(formControl.errors).map((key: string) => {
                    let controlLength = null;
                    switch (key) {
                        case AmountMaxValidatorKey:
                            controlLength = formControl.errors[key].max;
                            break;
                        case AmountMinValidatorKey:
                            controlLength = formControl.errors[key].min;
                            break;
                    }
                    return messagesFunction(control.placeholder, controlLength)[key];
                });
                return false;
            }
            return true;
        });

        let status = true;
        const tempModel = new PrizeGroupResponseModel(
            {
                start_position: Number(this.formGroup.value[PrizeGroupResponseModel.getStartPositionSelector()]),
                id: this.model?.id,
                per_position: Number(this.formGroup.value[PrizeGroupResponseModel.getPerPositionSelector()]),
                end_position: Number(this.formGroup.value[PrizeGroupResponseModel.getEndPositionSelector()]),
                trading_plan_id: this.plan.id,
                trace_identifier: this.model?.trace_identifier
            }
        );
        const startControl = this.controls.find(c => c.name == PrizeGroupResponseModel.getStartPositionSelector());
        const positionControl = this.controls.find(c => c.name == PrizeGroupResponseModel.getPerPositionSelector());
        let totalPercentage = 0;
        if (!this.plan.prizeWinnerCount) {
            startControl.setError(messagesFunction(startControl.placeholder)[PanelPlansEditMessages.PrizeGroupWinnerCountNotConfigured.key]);
            status = false;
        } else {
            startControl.removeError(messagesFunction(startControl.placeholder)[PanelPlansEditMessages.PrizeGroupWinnerCountNotConfigured.key]);
        }
        this.plan.prizeGroups.filter(x => this.model != null ? x.id != this.model.id : true).forEach((group: PrizeGroupResponseModel) => {
            if (group.getPositionsInRange().filter(x => tempModel.getPositionsInRange().includes(x)).length > 0) {
                startControl.setError(messagesFunction(startControl.placeholder)[PanelPlansEditMessages.OverlappedPrizeGroup.key]);
                status = false;
            } else {
                startControl.removeError(messagesFunction(startControl.placeholder)[PanelPlansEditMessages.OverlappedPrizeGroup.key]);
            }
            totalPercentage += group.per_position * group.getPositionsCount();
        });
        if (totalPercentage + tempModel.getTotalPositionPercentage() > 100) {
            positionControl.setError(messagesFunction(positionControl.placeholder)[PanelPlansEditMessages.TotalPercentageExceeded.key]);
            status = false;
        } else {
            positionControl.removeError(messagesFunction(positionControl.placeholder)[PanelPlansEditMessages.TotalPercentageExceeded.key]);
        }

        return statusesCheck.indexOf(false) < 0 && status;
    }
}
